import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss']
})
export class SliderComponent implements OnInit {
  @Input() min: number;
  @Input() max: number;
  @Input() steps: number;
  @Input() initialValue: number;

  @Input() game: string;

  @Input() set currentValue(value: number) {
    if (this.rangeSteps) {
      this.currentStep = this.getStepForPositionValue(undefined, value);
    }
  }

  @Output() slideEnd = new EventEmitter<number>();
  @Output() slideNext = new EventEmitter<number>();

  @ViewChild('handle', {static: true}) handleElem: ElementRef;
  @ViewChild('bar', {static: true}) barElem: ElementRef;

  private mouseUpEvents = ['mouseup', 'mouseleave', 'touchend', 'touchcancel'];
  private mouseDownEvents = ['mousedown', 'touchstart'];
  private mouseMoveEvents = ['mousemove', 'touchmove'];

  private allSteps: number[];
  private rangeSteps: { value: number, position: number }[];
  private oneStep: number;

  private boundingBoxLeft: number;
  private boundingBoxWidth: number;

  public currentStep;

  constructor() {
    this.onMove = this.onMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
  }

  ngOnInit() {
    this.attachDownListener();
    this.updateValues();

    if (this.initialValue) {
      this.currentStep = this.getStepForPositionValue(undefined, this.initialValue);
    }
  }

  private updateValues() {
    this.allSteps = this.calculateSteps();
    this.oneStep = 100 / (this.allSteps.length - 1);
    this.rangeSteps = this.calculatePositionsForValues();
  }

  private calculateSteps(): number[] {
    const rangeSteps = [];
    for (let i = this.min; i <= this.max; i += this.steps) {
      rangeSteps.push(i);
    }

    return rangeSteps;
  }

  private calculatePositionsForValues(): { value: number, position: number }[] {
    const positionValues = [];

    for (let i = 0; i < this.allSteps.length; i++) {
      positionValues.push({
        value: this.allSteps[i],
        position: i * this.oneStep
      });
    }

    return positionValues;
  }

  private getStepForPositionValue(position?: number, value?: number) {
    let step = null;
    if (position !== undefined) {
      step = this.rangeSteps.find(e => e.position === position);
    }
    if (value !== undefined) {
      step = this.rangeSteps.find(e => e.value === value);
    }

    return step;
  }

  private findClosestPosition(position: number) {
    return this.rangeSteps.reduce((prev: number, curr) => {
      return (Math.abs(curr.position - position) < Math.abs(prev - position) ? curr.position : prev);
    }, 0);
  }

  private attachUpListener() {
    this.mouseUpEvents.forEach(event => {
      document.addEventListener(event, this.onMouseUp);
    });
  }

  private detachUpListener() {
    this.mouseUpEvents.forEach(event => {
      document.removeEventListener(event, this.onMouseUp);
    });
  }

  private attachDownListener() {
    this.mouseDownEvents.forEach(event => {
      this.handleElem.nativeElement.addEventListener(event, this.onMouseDown);
    });
  }

  private detachDownListener() {
    this.mouseDownEvents.forEach(event => {
      this.handleElem.nativeElement.removeEventListener(event, this.onMouseDown);
    });
  }

  private attachMoveEventListener() {
    this.mouseMoveEvents.forEach(event => {
      document.addEventListener(event, this.onMove);
    });
  }

  private detachMoveEventListener() {
    this.mouseMoveEvents.forEach(event => {
      document.removeEventListener(event, this.onMove);
    });
  }

  private onMove(event: MouseEvent | TouchEvent) {
    event.stopPropagation();
    event.preventDefault();

    let clientX;
    if ((window as any).TouchEvent && event instanceof TouchEvent) {
      clientX = (event as TouchEvent).touches[0].clientX;
    } else {
      clientX = (event as MouseEvent).clientX;
    }
    const x = clientX - this.boundingBoxLeft;
    const percentX = x / this.boundingBoxWidth * 100;
    const targetX = this.findClosestPosition(percentX);

    if (!this.currentStep || this.currentStep.position !== targetX) {
      this.currentStep = this.getStepForPositionValue(targetX);
      this.slideNext.emit(this.currentStep.value);
    }
  }

  private onMouseUp() {
    this.attachDownListener();
    this.detachUpListener();
    this.detachMoveEventListener();
    this.slideEnd.emit(this.currentStep.value);
  }

  private onMouseDown() {
    this.detachDownListener();
    this.attachUpListener();
    this.attachMoveEventListener();

    this.boundingBoxLeft = this.barElem.nativeElement.getBoundingClientRect().left;
    this.boundingBoxWidth = this.barElem.nativeElement.getBoundingClientRect().width;
  }
}
